import axios from "axios";
const baseUrl = process.env.REACT_APP_BASE_URL;

// Helper function to determine correct login route based on user type
const getLoginRoute = () => {
  const user = localStorage.getItem('user');
  
  // If user exists in localStorage, they're an admin user
  if (user) {
    return '/admin/login';
  }
  // If customer exists or neither exists, use customer login
  return '/login';
};

const LOGIN_ROUTES = ['/login', '/admin/login', '/client/login'];

// When a session expires, several requests in flight at once (notification poll,
// station refresh, page queries, ...) can all 401 around the same time and each
// independently call forceLogout(). Without this guard, the first call correctly
// reads localStorage.user and resolves '/admin/login', then clears it — but a
// second call running right after would see 'user' already gone and wrongly
// resolve '/login', overwriting the first (correct) navigation. Only the first
// call may decide the route and clear storage; later ones are no-ops.
let loggingOut = false;

// Clear the client-side session and send the user back to the right login page.
// `expired` marks the logout as involuntary so the login page can explain why
// (see adminLogin.sessionExpired); a logout the user asked for passes false.
const forceLogout = (expired = true) => {
  if (loggingOut) return;
  loggingOut = true;

  // Route must be resolved BEFORE clearing storage — getLoginRoute() reads it
  const loginRoute = getLoginRoute();

  if (expired) {
    sessionStorage.setItem('sessionExpired', '1');
  }

  localStorage.removeItem('user');
  localStorage.removeItem('customer');
  localStorage.removeItem('defaultStation');
  localStorage.removeItem('token');

  if (!LOGIN_ROUTES.includes(window.location.pathname)) {
    window.location.href = loginRoute;
  }
};

// Track successful logins to prevent immediate logout
let lastSuccessfulLogin = 0;
const LOGIN_PROTECTION_WINDOW = 5000; // 5 seconds

// Track refresh attempts to prevent infinite retries
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 30000; // 30 seconds
let lastRefreshAttempt = 0;

// Function to mark successful login
export const markSuccessfulLogin = () => {
  lastSuccessfulLogin = Date.now();
  // Reset refresh attempts on successful login
  refreshAttempts = 0;
  lastRefreshAttempt = 0;
};

// Function to check if we're in the protection window
const isInLoginProtectionWindow = () => {
  return (Date.now() - lastSuccessfulLogin) < LOGIN_PROTECTION_WINDOW;
};

// Function to check if we can attempt refresh
const canAttemptRefresh = () => {
  const now = Date.now();
  
  // Reset attempts if cooldown period has passed
  if (now - lastRefreshAttempt > REFRESH_COOLDOWN) {
    refreshAttempts = 0;
  }
  
  return refreshAttempts < MAX_REFRESH_ATTEMPTS;
};

// Function to record refresh attempt
const recordRefreshAttempt = () => {
  refreshAttempts++;
  lastRefreshAttempt = Date.now();
};

// Single-flight refresh: รวม refresh requests ที่เกิดพร้อมกันให้เป็น promise เดียว
// ป้องกันการยิง refresh ซ้อนกันหลายตัวตอนหลายหน้า/หลาย API โดน 401 พร้อมกัน
let refreshPromise = null;

const apiInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true, // Enable cookies for cross-origin requests
  timeout: 30000, // 30 second timeout (increased for long operations)
});

// Request interceptor
apiInstance.interceptors.request.use(
  (config) => {
    // Cookies are automatically included with withCredentials: true
    // No need to manually add Authorization header
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for automatic token refresh
apiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // The refresh call itself is sent through this same apiInstance, so it also
    // passes through this interceptor. If it 401s (e.g. no/expired refresh token —
    // the normal "session actually expired" case) and we let it fall into the block
    // below, it would `await refreshPromise` — which is its own in-flight promise —
    // and deadlock forever. Reject it directly so the outer catch (below) can run.
    if (originalRequest?.url?.includes('/public/refresh-token')) {
      return Promise.reject(error);
    }

    // If request failed due to 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      // Was a refresh already in flight when we got here? If so, we're just
      // piggy-backing on it — we must NOT count this as our own attempt or
      // gate it behind the rate limiter, otherwise a burst of requests that
      // all 401 at once (e.g. several widgets re-fetching the moment a long-idle
      // tab regains focus) looks like N separate failures instead of the single
      // real refresh call it actually is, and forces a logout on a perfectly
      // valid refresh token.
      const joiningExistingRefresh = !!refreshPromise;

      if (!joiningExistingRefresh && !canAttemptRefresh()) {
        console.log('Max refresh attempts reached, forcing logout');

        // Force logout due to too many failed refresh attempts
        forceLogout();

        return Promise.reject(error);
      }

      try {
        // Single-flight: ถ้ามี refresh กำลังทำอยู่ ให้รอตัวเดิม ไม่ยิงซ้ำ
        if (!refreshPromise) {
          recordRefreshAttempt(); // Only the request that *initiates* the call counts
          refreshPromise = apiInstance.post('/public/refresh-token')
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;

        // Reset refresh attempts on successful refresh
        refreshAttempts = 0;
        lastRefreshAttempt = 0;

        // Retry the original request
        return apiInstance(originalRequest);
      } catch (refreshError) {
        // ถ้าเป็น network error (server กำลัง restart/deploy ยังไม่ขึ้น)
        // อย่านับเป็น failed attempt และอย่าเพิ่ง logout — ให้ user รอ/refresh เอง
        if (!refreshError.response) {
          console.log('Refresh failed due to network error, not forcing logout');
          if (!joiningExistingRefresh) {
            refreshAttempts = Math.max(0, refreshAttempts - 1);
          }
          return Promise.reject(refreshError);
        }

        // Refresh failed, redirect to login
        console.log('Token refresh failed, redirecting to login');

        // Check if we're in the login protection window
        if (isInLoginProtectionWindow()) {
          console.log('Skipping logout due to recent successful login');
          return Promise.reject(refreshError);
        }

        forceLogout();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export function api() {
  return apiInstance;
}

// Secure logout function
export const secureLogout = async () => {
  try {
    // baseURL is already '/api' — do not prefix with '/api' again here (that
    // previously resolved to /api/api/public/logout and 404'd, so the server-side
    // token/refresh-token were never blacklisted or revoked on logout).
    await apiInstance.post('/public/logout');
  } catch (error) {
    console.error('Logout request failed:', error);
  } finally {
    // Clear local storage regardless of API call result. This is a user-initiated
    // logout, not an expired session, so don't set the sessionExpired flag.
    forceLogout(false);
  }
};
